import { apiClient, getData, getPage, type Page } from '@/api/client'
import type { components, operations } from '@/api/schema'
import { CHART_ADJUSTED, CHART_RESOLUTION } from '@/features/market/api/marketApi'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** The query string an endpoint accepts, from the generated schema rather than restated. */
type Query<K extends keyof operations> = NonNullable<operations[K]['parameters']['query']>

/**
 * The window a `history/` read asks for, in epoch SECONDS.
 *
 * `countback` wins over `from` in mm-be (services/chart/bars.py), and `to` is
 * exclusive. The price basis is not part of it: it is the member's, passed beside it.
 */
export type HistoryWindow = Pick<
  Query<'api_v5_charts_history_retrieve'>,
  'from' | 'to' | 'countback'
>

/** The eleven study panes mm-be draws (services/chart/studies.py `STUDIES`). */
export type ChartStudy = operations['api_v5_charts_studies_retrieve']['parameters']['path']['study']

/**
 * A study read's own parameters: `broker` for `broker_track` (mm-be answers 400 without one),
 * `sessions` for `big_buyer_cost`. No window: mm-be's default, its latest 250 sessions.
 */
export type StudyParams = Pick<Query<'api_v5_charts_studies_retrieve'>, 'broker' | 'sessions'>

/** What one chart pane reads: a study, or the trade counts on `history/`'s daily bars. */
export type PaneRead =
  { kind: 'study'; study: ChartStudy; params: StudyParams } | { kind: 'trade-size' }

/**
 * Which prices a bar carries: adjusted for bonus and right issues (mm-be's default, and the basis
 * the stock page and the Signal Zone draw on), or as traded.
 */
export type PriceBasis = 'adjusted' | 'traded'

const ADJUSTED_PARAM = { adjusted: CHART_ADJUSTED, traded: 'false' } as const

/** The bar sizes `history/` builds; the library combines the rest of its menu from these. */
export type ChartResolution = '1D' | '1' | '5' | '15' | '60'

type SearchQuery = Query<'api_v5_charts_search_list'>
export type ChartSymbolType = NonNullable<SearchQuery['type']>
export type ChartExchange = NonNullable<SearchQuery['exchange']>

export interface ChartSearchParams {
  query: string
  type: ChartSymbolType
  exchange: ChartExchange
}

/**
 * Rows per symbol search: what the charting library asks for, and mm-be's own
 * default (services/chart/search.py `DEFAULT_LIMIT`). Its ceiling is 50.
 */
export const CHART_SEARCH_LIMIT = 30

/**
 * The 15 `/api/v5/charts/*` operations, one function each.
 *
 * Daily history goes out with the same `resolution` and `adjusted` as
 * `features/market`'s own `/charts/history/` read, so the full chart and the
 * stock page's candles are the same series, until the member switches the
 * full chart to as-traded prices. That read stays where it is,
 * because its shape differs: a bar count with no date window, and times in
 * seconds rather than this library's milliseconds.
 *
 * Template names are passed as they are. openapi-fetch already runs every
 * path parameter through `encodeURIComponent`, and mm-be routes the name as
 * `<path:name>` so an encoded "/" survives; encoding here as well would send
 * "%2520" for a space and miss every template.
 */
export const chartApi = {
  config: (): Promise<Wire<'ChartConfig'>> =>
    getData(() => apiClient.GET('/api/v5/charts/config/')),

  symbol: (symbol: string): Promise<Wire<'ChartSymbol'>> =>
    getData(() => apiClient.GET('/api/v5/charts/symbols/', { params: { query: { symbol } } })),

  search: ({ query, type, exchange }: ChartSearchParams): Promise<Page<Wire<'SymbolMatch'>>> =>
    getPage(() =>
      apiClient.GET('/api/v5/charts/search/', {
        params: { query: { query, type, exchange, limit: CHART_SEARCH_LIMIT } },
      }),
    ),

  history: (
    symbol: string,
    window: HistoryWindow,
    resolution: ChartResolution = CHART_RESOLUTION,
    basis: PriceBasis = 'adjusted',
  ): Promise<Wire<'BarSeries'>> =>
    getData(() =>
      apiClient.GET('/api/v5/charts/history/', {
        params: { query: { symbol, resolution, adjusted: ADJUSTED_PARAM[basis], ...window } },
      }),
    ),

  study: (study: ChartStudy, symbol: string, params: StudyParams): Promise<Wire<'StudySeries'>> =>
    getData(() =>
      apiClient.GET('/api/v5/charts/studies/{study}/', {
        params: { path: { study }, query: { symbol, ...params } },
      }),
    ),

  time: (): Promise<Wire<'ServerTime'>> => getData(() => apiClient.GET('/api/v5/charts/time/')),

  layouts: (): Promise<Page<Wire<'LayoutSummary'>>> =>
    getPage(() => apiClient.GET('/api/v5/charts/layouts/')),

  layout: (layoutId: number): Promise<Wire<'Layout'>> =>
    getData(() =>
      apiClient.GET('/api/v5/charts/layouts/{layout_id}/', {
        params: { path: { layout_id: layoutId } },
      }),
    ),

  createLayout: (body: Wire<'LayoutWriteRequest'>): Promise<Wire<'LayoutSummary'>> =>
    getData(() => apiClient.POST('/api/v5/charts/layouts/', { body })),

  updateLayout: (
    layoutId: number,
    body: Wire<'LayoutWriteRequest'>,
  ): Promise<Wire<'LayoutSummary'>> =>
    getData(() =>
      apiClient.PUT('/api/v5/charts/layouts/{layout_id}/', {
        params: { path: { layout_id: layoutId } },
        body,
      }),
    ),

  deleteLayout: (layoutId: number): Promise<Wire<'LayoutDeleted'>> =>
    getData(() =>
      apiClient.DELETE('/api/v5/charts/layouts/{layout_id}/', {
        params: { path: { layout_id: layoutId } },
      }),
    ),

  studyTemplates: (): Promise<Page<Wire<'StudyTemplateSummary'>>> =>
    getPage(() => apiClient.GET('/api/v5/charts/study-templates/')),

  studyTemplate: (name: string): Promise<Wire<'StudyTemplate'>> =>
    getData(() =>
      apiClient.GET('/api/v5/charts/study-templates/{name}/', { params: { path: { name } } }),
    ),

  saveStudyTemplate: (
    body: Wire<'StudyTemplateWriteRequest'>,
  ): Promise<Wire<'StudyTemplateSummary'>> =>
    getData(() => apiClient.POST('/api/v5/charts/study-templates/', { body })),

  deleteStudyTemplate: (name: string): Promise<Wire<'StudyTemplateDeleted'>> =>
    getData(() =>
      apiClient.DELETE('/api/v5/charts/study-templates/{name}/', { params: { path: { name } } }),
    ),
}
